import { useTranslation } from "react-i18next";
import { useRunsPublicProcurements } from "../../../common/organization/useRunsPublicProcurements";

/**
 * Resolves a home-dashboard copy key to its translated label.
 *
 * The home dashboard is data-driven: greeting keys (greeting.ts), hero action headlines /
 * labels / sublabels (homeConfig.tsx) and panel titles / empty states (panels/*) are all
 * resolved at runtime from a key. A plain `t(key)` is invisible to i18next-parser, so those
 * keys get pruned from the locale files on the next `npm run langen` — silently dropping
 * their Norwegian translations. Listing them here as literal `t("…")` calls (the useXxxLabel
 * pattern, cf. useNavLabel) keeps them extracted and translatable.
 */
export const useHomeLabel = () => {
  const { t } = useTranslation();
  const runsPublicProcurements = useRunsPublicProcurements();
  const privateOrganisationLabels: Record<string, string> = {
    "Start procurement": t("Start sourcing event"),
    "Create and announce": t("Create and invite"),
    Procurements: t("Sourcing"),
    "My active procurements": t("My active sourcing events"),
    "Procurements by phase": t("Sourcing events by phase"),
    "No procurements yet": t("No sourcing events yet"),
    "Recently opened procurements": t("Recently opened sourcing events"),
    "No active procurements yet": t("No active sourcing events yet"),
    "Open a procurement and it shows up here": t("Open a sourcing event and it shows up here"),
  };
  const labels: Record<string, string> = {
    // Greeting (greeting.ts)
    "Good morning": t("Good morning"),
    "Good afternoon": t("Good afternoon"),
    "Good evening": t("Good evening"),
    // Headlines (homeConfig.tsx)
    "What will you work on today?": t("What will you work on today?"),
    "What are you looking for?": t("What are you looking for?"),
    "Find your next opportunity": t("Find your next opportunity"),
    // Hero action labels (homeConfig.tsx)
    "Start procurement": t("Start procurement"),
    "Assess need": t("Assess need"),
    "Active agreements": t("Active agreements"),
    "Report need": t("Report need"),
    "My needs": t("My needs"),
    "Find procurements": t("Find procurements"),
    "My bids": t("My bids"),
    "My invitations": t("My invitations"),
    // Hero action sublabels (homeConfig.tsx)
    "Create and announce": t("Create and announce"),
    "Review reported needs": t("Review reported needs"),
    "Your live contracts": t("Your live contracts"),
    "Register a new need": t("Register a new need"),
    "Your registered needs": t("Your registered needs"),
    "Browse published competitions": t("Browse published competitions"),
    "Your offers in progress": t("Your offers in progress"),
    "Invitations to submit a bid": t("Invitations to submit a bid"),
    // Panel titles & empty states (panels/*)
    "Procurements": t("Procurements"),
    "My active procurements": t("My active procurements"),
    // Tab labels sit inside a card already titled "Anskaffelser", so they drop the noun.
    "Ongoing": t("Ongoing"),
    "Drafts": t("Drafts"),
    "Recently opened": t("Recently opened"),
    "My drafts": t("My drafts"),
    "Alerts": t("Alerts"),
    "Ask about your contracts…": t("Ask about your contracts…"),
    "Show earlier": t("Show earlier"),
    "Show later": t("Show later"),
    Deadlines: t("Deadlines"),
    "Agreements expiring": t("Agreements expiring"),
    "Procurements by phase": t("Procurements by phase"),
    "No procurements yet": t("No procurements yet"),
    "Past their deadline": t("Past their deadline"),
    "Announced or under evaluation, deadline gone by": t("Announced or under evaluation, deadline gone by"),
    "Agreements without an end date": t("Agreements without an end date"),
    "Within a month": t("Within a month"),
    "1–3 months": t("1–3 months"),
    "3–6 months": t("3–6 months"),
    "6–12 months": t("6–12 months"),
    "No agreements expire in the next year": t("No agreements expire in the next year"),
    "Recently opened procurements": t("Recently opened procurements"),
    "Recently opened contracts": t("Recently opened contracts"),
    "No active procurements yet": t("No active procurements yet"),
    "No active contracts": t("No active contracts"),
    "No drafts": t("No drafts"),
    "No needs yet": t("No needs yet"),
    "Open procurements": t("Open procurements"),
    "No invitations right now": t("No invitations right now"),
    "No offers yet": t("No offers yet"),
    "No open procurements right now": t("No open procurements right now"),
    "Nothing needs you right now": t("Nothing needs you right now"),
    "No deadlines coming up": t("No deadlines coming up"),
    "Open a procurement and it shows up here": t("Open a procurement and it shows up here"),
    "Open a contract and it shows up here": t("Open a contract and it shows up here"),
  };
  return (key: string): string =>
    (runsPublicProcurements ? undefined : privateOrganisationLabels[key]) ?? labels[key] ?? key;
};
